// v3.6.3 — "MERCEDES COCKPIT" : HUD livreur façon tableau de bord MBUX.
// Cluster d'instruments bas-centre : jauge de vitesse néon (SVG), distance
// restante en gros chiffres, ETA, destination, LED route, boutons métalliques.
// Partagé Mapbox + Leaflet. Testids stables préservés :
//   delivery-hud-card / delivery-hud-collapse-btn / delivery-hud-pill /
//   delivery-hud-pill-distance / delivery-hud-distance / delivery-hud-eta /
//   map-engine-badge / delivery-dest-code / delivery-dest-label /
//   delivery-recenter / delivery-refresh-route / delivery-status-arrived

import { useEffect, useRef, useState } from "react";
import { getPanelState, togglePanelState } from "@/lib/panelState";

const NEON = "#00FF88";
const NEON_DIM = "rgba(0,255,136,0.55)";
const AMBER = "#FFB020";
const GLASS = "linear-gradient(180deg, rgba(13,18,22,0.90) 0%, rgba(5,9,11,0.96) 100%)";
const FONT = "'Inter', system-ui, -apple-system, sans-serif";

function fmtDuration(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `${h}h ${rest}min`;
}

function fmtDistance(m) {
  if (m == null) return "—";
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(2)} km`;
}

const ENGINE_META = {
  mapbox: { badgeLabel: "MAPBOX 3D", routeOkLabel: "OSRM ✓" },
  leaflet: { badgeLabel: "CLASSIQUE", routeOkLabel: "directe ✓" },
};

// v3.6.5 — Turn-by-turn : libellé FR + flèche SVG par manœuvre OSRM.
function turnLabel(t) {
  const mod = t.modifier || "";
  if (t.type === "arrive") return "Tu arrives à destination";
  if (t.type === "roundabout" || t.type === "rotary") return t.exit ? `Au rond-point, ${t.exit}ᵉ sortie` : "Prends le rond-point";
  if (mod === "uturn") return "Fais demi-tour";
  if (mod === "left") return "Tourne à gauche";
  if (mod === "right") return "Tourne à droite";
  if (mod === "slight left") return "Serre à gauche";
  if (mod === "slight right") return "Serre à droite";
  if (mod === "sharp left") return "Tourne fort à gauche";
  if (mod === "sharp right") return "Tourne fort à droite";
  if (t.type === "merge") return "Insère-toi";
  return "Continue tout droit";
}

function fmtTurnDist(m) {
  if (m == null) return "";
  if (m >= 1000) return `dans ${(m / 1000).toFixed(1)} km`;
  if (m >= 20) return `dans ${Math.round(m / 10) * 10} m`;
  return "maintenant";
}

function TurnArrow({ type, modifier }) {
  let d = "M16 26 V8 M10 13 L16 7 L22 13"; // tout droit
  const mod = modifier || "";
  if (type === "arrive") d = "M16 26 V8 M10 13 L16 7 L22 13 M9 26 H23";
  else if (mod === "uturn") d = "M11 26 V14 Q11 7 16 7 Q21 7 21 14 V20 M17 17 L21 22 L25 17";
  else if (mod === "left") d = "M22 26 V16 Q22 12 18 12 H9 M13 7 L8 12 L13 17";
  else if (mod === "right") d = "M10 26 V16 Q10 12 14 12 H23 M19 7 L24 12 L19 17";
  else if (mod === "slight left") d = "M18 26 V16 L11 9 M11 16 V9 H18";
  else if (mod === "slight right") d = "M14 26 V16 L21 9 M21 16 V9 H14";
  else if (mod === "sharp left") d = "M20 26 V18 L8 12 M14 8 L8 12 L15 17";
  else if (mod === "sharp right") d = "M12 26 V18 L24 12 M18 8 L24 12 L17 17";
  else if (type === "roundabout" || type === "rotary") d = "M16 26 V20 M16 8 A6 6 0 1 1 10 14 M16 8 V4 M16 8 L20 5";
  return (
    <svg viewBox="0 0 32 32" width="34" height="34" aria-hidden
      style={{ flexShrink: 0, filter: `drop-shadow(0 0 6px ${NEON_DIM})` }}>
      <path d={d} fill="none" stroke={NEON} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Jauge de vitesse type MBUX : arc 240°, aiguille néon, chiffres tabulaires.
function SpeedGauge({ kmh }) {
  const MAX = 90;
  const v = Math.max(0, Math.min(MAX, kmh || 0));
  const startA = -210, endA = 30; // arc de 240°
  const angle = startA + (v / MAX) * (endA - startA);
  const polar = (deg, r) => {
    const rad = (deg * Math.PI) / 180;
    return [40 + r * Math.cos(rad), 40 + r * Math.sin(rad)];
  };
  const arc = (a0, a1, r) => {
    const [x0, y0] = polar(a0, r);
    const [x1, y1] = polar(a1, r);
    const large = a1 - a0 > 180 ? 1 : 0;
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
  };
  return (
    <div style={{ position: "relative", width: 84, height: 84, flexShrink: 0 }} data-testid="delivery-hud-speed-gauge">
      <svg viewBox="0 0 80 80" width="84" height="84" aria-hidden>
        <path d={arc(startA, endA, 32)} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="5" strokeLinecap="round" />
        <path d={arc(startA, angle, 32)} fill="none" stroke={NEON} strokeWidth="5" strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 5px ${NEON_DIM})`, transition: "d 0.6s ease" }} />
        {[0, 30, 60, 90].map((t) => {
          const a = startA + (t / MAX) * (endA - startA);
          const [x0, y0] = polar(a, 26);
          const [x1, y1] = polar(a, 30);
          return <line key={t} x1={x0} y1={y0} x2={x1} y2={y1} stroke="rgba(255,255,255,0.35)" strokeWidth="1.4" />;
        })}
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", pointerEvents: "none",
      }}>
        <div data-testid="delivery-hud-speed" style={{
          fontSize: 22, fontWeight: 800, color: "#F8FAFC", lineHeight: 1,
          fontFamily: FONT, fontFeatureSettings: "'tnum'",
          textShadow: `0 0 12px ${NEON_DIM}`,
        }}>{Math.round(v)}</div>
        <div style={{ fontSize: 8, color: "#7C8B94", letterSpacing: 1.5, marginTop: 2, fontWeight: 700 }}>KM/H</div>
      </div>
    </div>
  );
}

export default function DeliveryHUD({
  engine = "mapbox",
  remainingM,
  etaSec,
  routeStatus = "loading",
  destCode,
  destLabel,
  arrived = false,
  onRecenter,
  onRefreshRoute,
  engineFootnote = "",
}) {
  const [collapsed, setCollapsed] = useState(() => getPanelState("delivery_hud", "expanded") === "collapsed");
  const toggle = () => setCollapsed(togglePanelState("delivery_hud") === "collapsed");

  // Live nav : progression + vitesse lissée depuis les ticks aya:nav:progress.
  const [liveProgress, setLiveProgress] = useState(null);
  const [speedKmh, setSpeedKmh] = useState(0);
  const lastTickRef = useRef(null);
  useEffect(() => {
    const onProgress = (e) => {
      const d = e?.detail;
      if (!d || typeof d.remainingMeters !== "number") { setLiveProgress(null); return; }
      setLiveProgress({ remainingM: Math.round(d.remainingMeters), etaSec: Math.round(d.etaSec || 0), offRoute: !!d.offRoute, nextTurn: d.nextTurn || null });
      // Vitesse réelle = Δprogress/Δt (lissée), fallback speedMps du moteur.
      const now = Date.now();
      const prev = lastTickRef.current;
      lastTickRef.current = { t: now, p: d.progressMeters || 0 };
      let mps = typeof d.speedMps === "number" ? d.speedMps : 0;
      if (prev && now - prev.t > 300 && typeof d.progressMeters === "number") {
        const inst = ((d.progressMeters - prev.p) / (now - prev.t)) * 1000;
        if (Number.isFinite(inst) && inst >= 0 && inst < 45) mps = inst;
      }
      setSpeedKmh((old) => old * 0.6 + mps * 3.6 * 0.4);
    };
    const onEnd = () => { setLiveProgress(null); setSpeedKmh(0); lastTickRef.current = null; };
    window.addEventListener("aya:nav:progress", onProgress);
    window.addEventListener("aya:nav:end", onEnd);
    return () => {
      window.removeEventListener("aya:nav:progress", onProgress);
      window.removeEventListener("aya:nav:end", onEnd);
    };
  }, []);
  const liveRemainingM = liveProgress?.remainingM ?? remainingM;
  const liveEtaSec = liveProgress?.etaSec ?? etaSec;
  const offRoute = liveProgress?.offRoute === true;

  const meta = ENGINE_META[engine] || ENGINE_META.mapbox;
  const routeLedColor = routeStatus === "ok" ? NEON : routeStatus === "fallback" ? AMBER : "#64748B";

  // v3.6.4 — Type d'engin (moto/voiture) : persisté + notifié à la carte.
  const [vehicle, setVehicle] = useState(() => {
    try { return localStorage.getItem("civ_courier_vehicle") === "car" ? "car" : "moto"; } catch (_) { return "moto"; }
  });
  const pickVehicle = (v) => {
    setVehicle(v);
    try { localStorage.setItem("civ_courier_vehicle", v); } catch (_) { /* noop */ }
    try { window.dispatchEvent(new CustomEvent("aya:vehicle:changed", { detail: { vehicle: v } })); } catch (_) { /* noop */ }
  };
  const vehicleBtn = (v, label, icon) => (
    <button
      type="button"
      data-testid={`delivery-vehicle-${v}`}
      onClick={() => pickVehicle(v)}
      aria-label={label}
      aria-pressed={vehicle === v}
      style={{
        padding: "3px 9px", borderRadius: 999, fontSize: 12, lineHeight: 1.3, cursor: "pointer",
        border: `1px solid ${vehicle === v ? "rgba(0,255,136,0.55)" : "rgba(255,255,255,0.12)"}`,
        background: vehicle === v ? "rgba(0,255,136,0.14)" : "transparent",
        color: vehicle === v ? NEON : "#7C8B94",
      }}
    >{icon}</button>
  );

  const metalBtn = {
    width: 40, height: 40, borderRadius: "50%",
    background: "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.03))",
    border: "1px solid rgba(255,255,255,0.16)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 4px 12px rgba(0,0,0,0.4)",
    color: "#E8EDF0", fontSize: 15, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  };

  return (
    <>
      {/* Pill compact (collapsed, masqué si arrived) — bas-centre, néon. */}
      {collapsed && !arrived && (
        <button
          type="button"
          data-testid="delivery-hud-pill"
          onClick={toggle}
          aria-label="Afficher le cockpit de navigation"
          style={{
            position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", zIndex: 45,
            display: "flex", alignItems: "center", gap: 10,
            background: GLASS, color: "#F1F5F9",
            padding: "10px 18px", borderRadius: 999,
            border: `1px solid rgba(0,255,136,0.35)`,
            boxShadow: `0 10px 30px rgba(0,0,0,0.55), 0 0 18px rgba(0,255,136,0.12)`,
            backdropFilter: "blur(14px)",
            fontFamily: FONT,
            fontSize: 13, fontWeight: 800,
            cursor: "pointer",
            fontFeatureSettings: "'tnum'",
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: NEON, boxShadow: `0 0 8px ${NEON}` }} aria-hidden />
          <span data-testid="delivery-hud-pill-distance">{fmtDistance(liveRemainingM)}</span>
          <span style={{ color: "#3C4A52" }}>|</span>
          <span style={{ color: NEON }}>{fmtDuration(liveEtaSec)}</span>
          <span style={{ color: "#7C8B94", marginLeft: 2 }}>⌄</span>
        </button>
      )}

      {/* CLUSTER COCKPIT (expanded, ou toujours visible quand arrived). */}
      <div
        data-testid="delivery-hud-card"
        style={{
          position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)", zIndex: 45,
          width: "min(96vw, 560px)",
          background: GLASS,
          borderRadius: 22,
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.10)",
          backdropFilter: "blur(18px)",
          fontFamily: FONT,
          color: "#F8FAFC",
          display: (collapsed && !arrived) ? "none" : "block",
          overflow: "hidden",
        }}
      >
        {/* Trait néon signature en haut du cluster. */}
        <div aria-hidden style={{
          height: 2,
          background: `linear-gradient(90deg, transparent 0%, ${NEON} 22%, ${NEON} 78%, transparent 100%)`,
          boxShadow: `0 0 12px ${NEON_DIM}`,
          opacity: 0.9,
        }} />

        {/* Rangée méta : badge moteur · LED route · chevron. */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              data-testid="map-engine-badge"
              data-engine={engine}
              style={{
                fontSize: 8.5, fontWeight: 800, letterSpacing: 2, color: "#8FA0AA",
                textTransform: "uppercase",
              }}
            >{meta.badgeLabel}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: routeLedColor, boxShadow: `0 0 7px ${routeLedColor}` }} />
              <span style={{ fontSize: 9, fontWeight: 700, color: routeLedColor, letterSpacing: 0.6 }}>
                {routeStatus === "ok" ? meta.routeOkLabel : routeStatus === "fallback" ? "directe" : "…"}
              </span>
            </div>
            {offRoute && (
              <span style={{ fontSize: 9.5, fontWeight: 800, color: AMBER, letterSpacing: 0.5 }}>⚠ HORS ITINÉRAIRE</span>
            )}
          </div>
          {!arrived && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ display: "flex", gap: 4 }} data-testid="delivery-vehicle-switch">
                {vehicleBtn("moto", "Moto", "🏍")}
                {vehicleBtn("car", "Voiture", "🚗")}
              </div>
              <button
                type="button"
                data-testid="delivery-hud-collapse-btn"
                onClick={toggle}
                title="Réduire"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#7C8B94",
                  fontSize: 14, lineHeight: 1, cursor: "pointer", padding: 4,
                }}
              >⌄</button>
            </div>
          )}
        </div>

        {arrived ? (
          <div data-testid="delivery-status-arrived" style={{
            fontSize: 19, fontWeight: 800, color: NEON, padding: "14px 20px 18px",
            textShadow: `0 0 14px ${NEON_DIM}`, textAlign: "center",
          }}>
            🎯 Vous êtes arrivé !
          </div>
        ) : (
          <>
          {/* v3.6.5 — PROCHAINE MANŒUVRE : flèche néon + « Tourne à droite dans 200 m » */}
          {liveProgress?.nextTurn && (
            <div data-testid="delivery-next-turn" style={{
              display: "flex", alignItems: "center", gap: 12,
              margin: "8px 16px 2px", padding: "8px 12px",
              background: "rgba(0,255,136,0.06)",
              border: "1px solid rgba(0,255,136,0.18)",
              borderRadius: 14,
            }}>
              <TurnArrow type={liveProgress.nextTurn.type} modifier={liveProgress.nextTurn.modifier} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15.5, fontWeight: 800, lineHeight: 1.2 }}>
                  {turnLabel(liveProgress.nextTurn)}{" "}
                  <span data-testid="delivery-next-turn-dist" style={{ color: NEON, fontFeatureSettings: "'tnum'" }}>{fmtTurnDist(liveProgress.nextTurn.inMeters)}</span>
                </div>
                {liveProgress.nextTurn.name && (
                  <div style={{ fontSize: 10.5, color: "#8FA0AA", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    → {liveProgress.nextTurn.name}
                  </div>
                )}
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "6px 16px 14px" }}>
            {/* Jauge de vitesse (gauche, façon compteur MBUX) */}
            <SpeedGauge kmh={speedKmh} />

            {/* Bloc central : distance + ETA + destination */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <div data-testid="delivery-hud-distance" style={{
                  fontSize: 30, fontWeight: 300, letterSpacing: -0.5, lineHeight: 1.05,
                  fontFeatureSettings: "'tnum'", color: "#FFFFFF",
                }}>
                  {fmtDistance(liveRemainingM)}
                </div>
                <div data-testid="delivery-hud-eta" style={{
                  fontSize: 14, fontWeight: 800, color: NEON,
                  textShadow: `0 0 10px rgba(0,255,136,0.35)`,
                  fontFeatureSettings: "'tnum'",
                }}>{fmtDuration(liveEtaSec)}</div>
              </div>
              {(destCode || destLabel) && (
                <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                  <span aria-hidden style={{ color: NEON, fontSize: 10 }}>◉</span>
                  {destCode && <code data-testid="delivery-dest-code" style={{ fontSize: 10.5, color: "#B7C4CC", background: "rgba(255,255,255,0.06)", padding: "1px 6px", borderRadius: 5 }}>{destCode}</code>}
                  {destLabel && <div data-testid="delivery-dest-label" style={{
                    fontSize: 11, color: "#8FA0AA", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{destLabel}</div>}
                </div>
              )}
            </div>

            {/* Boutons métalliques (droite) */}
            {(onRecenter || onRefreshRoute) && (
              <div style={{ display: "flex", gap: 8 }}>
                {onRecenter && (
                  <button onClick={onRecenter} data-testid="delivery-recenter" title="Recentrer" aria-label="Recentrer la carte" style={metalBtn}>🧭</button>
                )}
                {onRefreshRoute && (
                  <button onClick={onRefreshRoute} data-testid="delivery-refresh-route" title="Rafraîchir l'itinéraire" aria-label="Rafraîchir l'itinéraire" style={metalBtn}>🔄</button>
                )}
              </div>
            )}
          </div>
          </>
        )}
        {engineFootnote && (
          <div style={{ padding: "0 16px 8px", fontSize: 8, color: "#4B5A63", letterSpacing: 0.8, textTransform: "uppercase" }}>{engineFootnote}</div>
        )}
      </div>
    </>
  );
}
